import { Typography } from '../../../components/typography/typography';
import { Toggle } from '../../../components/form/toggle';
import { Checkbox } from '../../../components/checkbox/checkbox';
import { kebabCase } from 'lodash';
import { permissionsGroups } from '../config/permissions';
import { useOutletContext } from 'react-router-dom';
import { AdminViewContext } from './admin-user-view';
import { useAdminPermissions } from '../hooks/use-admin-permissions';
import { useAdmin } from '../../../hooks/use-admin-context';
import { useAdminLocations } from '../hooks/use-admin-locations';
import SkeletonLoader from '../../../components/skeleton-loader/skeleton-loader';
import { useAdminEmailNotifications } from '../hooks/use-admin-email-notifications';

export const AdminUserPermissions = () => {
  const { admin } = useOutletContext<AdminViewContext>();

  const adminLogged = useAdmin();
  const isUpdateDisabled = adminLogged.id === admin?.adminId;

  const {
    isLoading: isLoadingLocations,
    allAvailableLocations,
    selectedLocations,
    areAllLocationsSelected,
    toggleLocation,
    toggleAllLocations,
  } = useAdminLocations({ adminId: admin?.adminId });

  const {
    isLoading: isLoadingPermissions,
    selectedPermissions,
    areAllPermissionsSelected,
    togglePermission,
    toggleAllPermissions,
  } = useAdminPermissions({ adminId: admin?.adminId });

  const { removeAllEmailsForLocation, removeAllEmailsForLocations } = useAdminEmailNotifications({
    adminId: admin?.adminId,
    adminLocations: selectedLocations.map((l) => l.id),
    fetchAtMount: false,
  });

  return (
    <div className="tab-secti flex flex-col">
      <div className="flex w-full justify-between items-center">
        <div className="tab-title">
          <Typography type="text-xl" weight="semi-bold">
            Locations Management
          </Typography>
        </div>
        <div className="flex">
          <div className="mb-1 mr-2">
            <span className="font-sans text-sm text-black-800">Select All</span>
          </div>
          <Toggle
            onClick={() => {
              if (!isUpdateDisabled) {
                const wasAllSelected = areAllLocationsSelected;
                toggleAllLocations();

                // If we're deselecting all locations, remove all email notifications
                if (wasAllSelected) {
                  const allLocationIds = allAvailableLocations.map((location) => location.id);
                  removeAllEmailsForLocations(allLocationIds);
                }
              }
            }}
            color="var(--quipli-yellow-300)"
            checked={areAllLocationsSelected}
            disabled={isUpdateDisabled}
            data-cy="select-all-locations-toggle"
            name="select-all-locations-toggle"
          />
        </div>
      </div>

      <div className="list-item bg-white">
        <div className="mb-3">
          <Typography type="text-md" weight="semi-bold" className="text-grey-800">
            Shop Locations
          </Typography>
        </div>

        {isLoadingLocations ? (
          <div data-cy="locations-skeleton-loader">
            <SkeletonLoader num={2} style={{ width: '100%', height: '2rem' }} />
          </div>
        ) : (
          <>
            {allAvailableLocations.map((location: any, index: number) => (
              <div key={index} className="flex mb-2">
                <Checkbox
                  isChecked={
                    selectedLocations.find((selectedLocation) => selectedLocation.id === location.id) !== undefined
                  }
                  onChange={(isChecked) => {
                    if (!isUpdateDisabled) {
                      toggleLocation(location, isChecked);

                      // If location is being unchecked, remove all email notifications for it
                      if (!isChecked) {
                        removeAllEmailsForLocation(location.id);
                      }
                    }
                  }}
                  isDisabled={isUpdateDisabled}
                  dataCy={`location-checkbox-${location.id}`}
                  name={`location-checkbox-${location.id}`}
                />
                <Typography type="text-sm" weight="regular" className="text-grey-700">
                  {location.name}
                </Typography>
              </div>
            ))}
          </>
        )}
      </div>

      <div className="flex w-full justify-between items-center">
        <div className="tab-title">
          <Typography type="text-xl" weight="semi-bold">
            Permissions Setup
          </Typography>
        </div>
        <div className="flex">
          <div className="mb-1 mr-2">
            <span className="font-sans text-sm text-black-800">Select All</span>
          </div>
          <Toggle
            onClick={() => {
              if (!isUpdateDisabled) {
                toggleAllPermissions();
              }
            }}
            checked={areAllPermissionsSelected}
            disabled={isUpdateDisabled}
            color="var(--quipli-yellow-300)"
            data-cy="select-all-permissions-toggle"
            name="select-all-permissions-toggle"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {isLoadingPermissions ? (
          <div data-cy="permissions-skeleton-loader">
            <SkeletonLoader num={8} style={{ flex: 'calc(50% - 10px)', height: '18rem' }} direction="row" />
          </div>
        ) : (
          <>
            {permissionsGroups.map((permissionsGroup: any[], i: number) => (
              <div key={`groups${i}`} className="grid gap-4">
                {permissionsGroup.map((elem: any, idx: number) => (
                  <div key={idx} className="permission-group">
                    <div className="mb-3">
                      <Typography type="text-md" weight="semi-bold" className="text-grey-800">
                        {elem.title}
                      </Typography>
                    </div>

                    {elem.permissions.map((permission: string, index: number) => (
                      <div key={idx + '-' + index} className="flex mb-2">
                        <Checkbox
                          isChecked={selectedPermissions.includes(permission)}
                          onChange={(isChecked) => {
                            if (!isUpdateDisabled) {
                              togglePermission(permission, isChecked);
                            }
                          }}
                          isDisabled={isUpdateDisabled}
                          dataCy={`permission-checkbox-${kebabCase(permission)}`}
                        />
                        <Typography type="text-sm" weight="regular" className="text-grey-700">
                          {permission}
                        </Typography>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
};
